import store from '@/store';
import {
  VuexModule, Module, getModule, Action, Mutation,
} from 'vuex-module-decorators';
import { getCompany, getCompanyInfo, getCompanySearch } from '@/api/Company';
import { jobListItemsSearch } from '@/api/Profession';
import { JobType } from '@/types/ProfessionType';
import { getCompanyClustersCount } from '@/api/Clusters';

export interface ICompanyState {
  company: []
  companyJobs: any
  companyJobParams: any
  companyJobFilter: any
  companyJobPagination: any
  companyJobSort: any
  companyJobSearch: any
  companyItem: any
  companyClusterParams: any
  searchJobCompany: any
}

const DEFAULT_COMPANY_CLUSTER_PARAMS = {
  cluster_id: null,
  cluster_name: null,
};

@Module({ dynamic: true, store, name: 'CompanyModule' })
class Company extends VuexModule implements ICompanyState {
  public company = [] as any;

  public companyItem = {} as any;

  public companyJobs = {
    amt: 0,
    firms: [],
    jobs: [],
  } as any;

  companyParams = {
    name: '',
    radius: 0,
  } as any;

  companyClusterParams = { ...DEFAULT_COMPANY_CLUSTER_PARAMS } as any;

  companyJobParams = {
    id: 0,
    jobnews_type: 1,
  } as any;

  companyJobFilter = {
    dejob: 0,
    recruit: 0,
    tmpjob: 0,
    workjob: 0,
    edujob: 0,
    praktikum: 0,
    fulltime: 0,
    parttime: 0,
    minijob: 0,
    seasonal: 0,
    holiday: 0,
    time_factor: 0,
    specialjob: 0,
    disability_friendly: 0,
    skill_level1: 0,
    skill_level2: 0,
    skill_level3: 0,
    partner_jobs_only: 0,
    other_jobs: 0,
    state_id: 0,
    cluster_id: 0,
  } as any;

  companyJobPagination = {
    per_page: 20,
    page: 1,
  } as any;

  companyJobSort = {
    date: null,
    name: 1,
  } as any;

  companyJobSearch = {
    profession_id: null,
    location_id: null,
    idcomp: null,
    job_location: null,
    radius: 0,
    jobnews_type: null,
    fulltext: 0,
    q: '',
    mode: 'AND',
    qmode: 1,
  } as any;

  companyType = JobType.items

  searchJobCompany = null;

  @Mutation
  SET_SEARCH_JOB_COMPANY(payload: any) {
    this.searchJobCompany = payload;
  }

  @Mutation
  SET_FILTER_DEFAULT() {
    Object.keys(this.companyJobFilter).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(this.companyJobFilter, key)) {
        this.companyJobFilter[key] = 0;
      }
    });
  }

  @Mutation
  SET_PARAMS_DEFAULT() {
    this.companyJobPagination.page = 1;
    this.companyJobPagination.per_page = 20;
    this.companyJobSort.date = null;
    this.companyJobSort.name = 1;
    this.companyJobParams.jobnews_type = 1;
  }

  @Mutation
  SET_SEARCH_DEFAULT() {
    this.companyJobSearch.profession_id = null;
    this.companyJobSearch.location_id = null;
    this.companyJobSearch.idcomp = null;
    this.companyJobSearch.radius = 0;
    this.companyJobSearch.jobnews_type = null;
    this.companyJobSearch.fulltext = 0;
    this.companyJobSearch.q = '';
    this.companyJobSearch.mode = 'AND';
    this.companyJobSearch.qmode = 1;
  }

  @Mutation
  SET_QUERY_SEARCH_DEFAULT() {
    this.searchJobCompany = null;
    this.companyParams.name = '';
    this.companyParams.radius = 0;
  }

  @Mutation
  SET_COMPANY_CLUSTER(payload: any) {
    this.companyClusterParams = { ...payload };
  }

  @Mutation
  RESET_COMPANY_CLUSTER() {
    this.companyClusterParams = { ...DEFAULT_COMPANY_CLUSTER_PARAMS };
  }

  @Mutation
  private SET_COMPANY_ITEM(item: any) {
    this.companyItem = item;
  }

  @Mutation
  private UPDATE_COMPANY_TYPE(payload: number) {
    this.companyType = payload;
  }

  @Mutation
  private UPDATE_PAGINATION(payload: any) {
    const { obj, value } = payload;
    this.companyJobPagination[obj] = value;
  }

  @Mutation
  private UPDATE_SEARCH(payload: any) {
    const { obj, value } = payload;
    this.companyJobSearch[obj] = value;
  }

  @Mutation
  private UPDATE_FILTER(payload: any) {
    const { obj, value } = payload;
    this.companyJobFilter[obj] = value;
  }

  @Mutation
  private UPDATE_SORT(payload: any) {
    const { obj, value } = payload;
    this.companyJobSort[obj] = value;
  }

  @Mutation
  private UPDATE_COMPANY_PARAMS(payload: any) {
    const { obj, value } = payload;
    this.companyParams[obj] = value;
  }

  @Mutation
  SET_COMPANY(items: any) {
    this.company = items;
  }

  @Mutation
  SET_COMPANY_JOBS(item: any) {
    this.companyJobs = item;
  }

  @Action
  async SetSearchJobCompany(payload: any) {
    this.SET_SEARCH_JOB_COMPANY(payload);
  }

  @Action({ rawError: true })
  async GetCompanyJobsSearch(payload: any) {
    const params = {
      ...payload,
      ...this.companyJobSearch,
      ...this.companyJobParams,
      ...this.companyJobFilter,
      ...this.companyJobPagination,
    };
    const request = await jobListItemsSearch(params);
    this.SET_COMPANY_JOBS(request);
  }

  @Action({ rawError: true })
  async GetCompanyJobs(payload: any) {
    const params = {
      ...this.companyJobFilter,
      ...this.companyJobSearch,
      ...payload,
    };
    const request = await getCompany(params);
    this.SET_COMPANY(request);
  }

  @Action({ rawError: true })
  async GetCompanySearch(payload: any) {
    const params = {
      ...payload,
      ...this.companyJobFilter,
      ...this.companyJobSearch,
      ...this.companyParams,
    };
    const request = await getCompanySearch(params);
    this.SET_COMPANY(request);
  }

  @Action({ rawError: true })
  async GetCompanyItem(payload: any) {
    const params = {
      ...payload,
      idcomp: this.companyJobSearch.idcomp,
    };
    const request = await getCompanyInfo(params);
    this.SET_COMPANY_ITEM(request);
  }

  @Action
  async SetPagination(payload: any) {
    this.UPDATE_PAGINATION(payload);
  }

  @Action
  async SetSort(payload: any) {
    this.UPDATE_SORT(payload);
  }

  @Action
  async SetSearch(payload: any) {
    this.UPDATE_SEARCH(payload);
  }

  @Action
  async SetFilter(payload: any) {
    this.UPDATE_FILTER(payload);
  }

  @Action
  async SetCompanyParams(payload: any) {
    this.UPDATE_COMPANY_PARAMS(payload);
  }

  @Action
  async SetCompanyType(payload: number) {
    this.UPDATE_COMPANY_TYPE(payload);
  }

  @Action
  async SetParamsDefault() {
    this.SET_FILTER_DEFAULT();
    this.SET_SEARCH_DEFAULT();
    this.SET_PARAMS_DEFAULT();
    this.RESET_COMPANY_CLUSTER();
    this.SET_QUERY_SEARCH_DEFAULT();
  }

  @Action
  async SetDefaultCompanyItems() {
    this.SET_COMPANY([]);
  }

  @Action
  async SetDefaultCompanyJobsItems() {
    this.SET_COMPANY_JOBS({
      amt: 0,
      firms: [],
      jobs: [],
    });
  }
}

export const CompanyModule = getModule(Company);
